import type { PageCommandResult } from '../types/global';

type PendingPageCommand = {
  pending: boolean;
  commandId?: string;
  action?: string;
  payload?: unknown;
};

const PAGE_COMMAND_MESSAGE = 'idsys.alty.page-command';
const PAGE_COMMAND_RESULT_MESSAGE = 'idsys.alty.page-command.result';
const PAGE_COMMAND_HTTP_MESSAGE = 'idsys.alty.page-command.http';
const PAGE_COMMAND_HTTP_RESULT_MESSAGE = 'idsys.alty.page-command.http.result';
const PAGE_COMMAND_TIMEOUT_MS = 15000;
const SCREENSHOT_COMMAND_TIMEOUT_MS = 20000;

type ViewportScreenshot = {
  ok: boolean;
  base64?: string;
  mimeType?: string;
  widthPx?: number;
  heightPx?: number;
  error?: string;
};

type Html2CanvasFn = (
  element: HTMLElement,
  options?: Record<string, unknown>,
) => Promise<HTMLCanvasElement>;

let html2canvasLoading: Promise<Html2CanvasFn | null> | null = null;

function frameWindow(frame: HTMLIFrameElement | null): Window {
  if (!frame?.contentWindow) throw new Error('App iframe is not ready');
  return frame.contentWindow;
}

function frameDocument(frame: HTMLIFrameElement | null): Document {
  const doc = frameWindow(frame).document;
  if (!doc) throw new Error('App iframe document is not ready');
  return doc;
}

function parsePayload(payload: unknown): unknown {
  if (typeof payload !== 'string') return payload;
  if (!payload.trim()) return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
}

function commandPayload(action: string, payload: unknown): unknown {
  const parsed = parsePayload(payload);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const envelope = parsed as Record<string, unknown>;
    if (typeof envelope.action === 'string' && envelope.action === action && 'payload' in envelope) {
      return envelope.payload;
    }
  }
  return parsed;
}

function textPayload(payload: unknown): string {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    return String(obj.url || obj.href || obj.path || obj.selector || obj.text || obj.value || '');
  }
  return '';
}

function isVisible(element: Element): boolean {
  const html = element as HTMLElement;
  const rect = html.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && !!(html.offsetParent || getComputedStyle(html).position === 'fixed');
}

function cssEscape(value: string): string {
  if (window.CSS?.escape) return window.CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}

function findByText(doc: Document, text: string): HTMLElement | null {
  const needle = text.trim().toLowerCase();
  if (!needle) return null;
  const candidates = doc.querySelectorAll<HTMLElement>('button, a, [role="button"], .btn, input[type="button"], input[type="submit"]');
  for (const candidate of candidates) {
    const candidateText = (candidate.textContent || (candidate as HTMLInputElement).value || '').trim().toLowerCase();
    if (candidateText.includes(needle)) return candidate;
  }
  return null;
}

function findElement(doc: Document, selectorOrText: string): HTMLElement | null {
  if (selectorOrText.startsWith('text:')) return findByText(doc, selectorOrText.slice(5));
  try {
    return doc.querySelector<HTMLElement>(selectorOrText);
  } catch {
    return findByText(doc, selectorOrText);
  }
}

function normalizeNavigateUrl(win: Window, payload: unknown): string {
  const raw = textPayload(payload);
  if (!raw) throw new Error('navigate requires payload.url, payload.href, payload.path, or a URL string');
  const nextUrl = new URL(raw, win.location.href);
  if (nextUrl.origin !== win.location.origin) throw new Error(`Refusing cross-origin navigation to ${nextUrl.origin}`);
  return nextUrl.href;
}

export function navigationTargetUrl(rawPayload: unknown, baseUrl?: string | null): string | null {
  const payload = commandPayload('navigate', rawPayload);
  const raw = textPayload(payload);
  if (!raw) return null;
  try {
    return new URL(raw, baseUrl || window.location.href).href;
  } catch {
    return null;
  }
}

function buildSnapshot(frame: HTMLIFrameElement | null) {
  const win = frameWindow(frame);
  const doc = frameDocument(frame);
  const snapshot: Record<string, unknown> = {
    url: win.location.href,
    title: doc.title,
    headings: [] as string[],
    buttons: [] as unknown[],
    links: [] as unknown[],
    inputs: [] as unknown[],
    selects: [] as unknown[],
    tables: [] as unknown[],
    agGrid: null,
  };

  doc.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((heading) => {
    const text = heading.textContent?.trim();
    if (text && text.length < 200) (snapshot.headings as string[]).push(`${heading.tagName}: ${text}`);
  });

  doc.querySelectorAll<HTMLElement>('button, [role="button"], input[type="button"], input[type="submit"], .btn').forEach((element) => {
    if (!isVisible(element)) return;
    const text = (element.textContent || (element as HTMLInputElement).value || '').trim();
    if (!text || text.length > 100) return;
    (snapshot.buttons as unknown[]).push({
      text,
      selector: element.id ? `#${cssEscape(element.id)}` : '',
      disabled: (element as HTMLButtonElement).disabled || false,
    });
  });

  doc.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((element) => {
    if (!isVisible(element)) return;
    const text = element.textContent?.trim() || '';
    const href = element.getAttribute('href') || '';
    if (!text || text.length > 100 || href === '#' || href.startsWith('javascript:')) return;
    (snapshot.links as unknown[]).push({ text, href });
  });

  doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach((element) => {
    if (!isVisible(element) || (element as HTMLInputElement).type === 'hidden') return;
    const label = element.id ? doc.querySelector(`label[for="${cssEscape(element.id)}"]`)?.textContent?.trim() : '';
    (snapshot.inputs as unknown[]).push({
      label: label || element.placeholder || element.name || '',
      type: (element as HTMLInputElement).type || 'text',
      value: element.value || '',
      selector: element.id ? `#${cssEscape(element.id)}` : element.name ? `[name="${cssEscape(element.name)}"]` : '',
    });
  });

  doc.querySelectorAll<HTMLSelectElement>('select').forEach((element) => {
    if (!isVisible(element)) return;
    const label = element.id ? doc.querySelector(`label[for="${cssEscape(element.id)}"]`)?.textContent?.trim() : '';
    (snapshot.selects as unknown[]).push({
      label: label || element.name || '',
      selected: element.value,
      options: Array.from(element.options).map((option) => option.text).slice(0, 10),
      selector: element.id ? `#${cssEscape(element.id)}` : '',
    });
  });

  const grid = doc.querySelector('.ag-root-wrapper');
  if (grid) {
    snapshot.agGrid = {
      columns: Array.from(grid.querySelectorAll('.ag-header-cell-text')).map((item) => item.textContent?.trim()).filter(Boolean),
      visibleRowCount: grid.querySelectorAll('.ag-row').length,
      pagingInfo: grid.querySelector('.ag-paging-description')?.textContent?.trim() || null,
    };
  }

  return snapshot;
}

function getGridData(frame: HTMLIFrameElement | null) {
  const doc = frameDocument(frame);
  const grid = doc.querySelector('.ag-root-wrapper');
  if (!grid) return { error: 'No AG Grid found on page' };
  const headers = Array.from(grid.querySelectorAll('.ag-header-cell-text')).map((item) => item.textContent?.trim() || '');
  const rows = Array.from(grid.querySelectorAll('.ag-row')).slice(0, 50).map((row) => {
    const values: Record<string, string> = {};
    row.querySelectorAll('.ag-cell').forEach((cell, index) => {
      values[headers[index] || `col${index}`] = cell.textContent?.trim() || '';
    });
    return values;
  });
  return { columns: headers, rows, note: 'Scraped from visible DOM' };
}

function loadHtml2canvas(doc: Document): Promise<Html2CanvasFn | null> {
  const win = doc.defaultView as (Window & { html2canvas?: Html2CanvasFn }) | null;
  if (!win) return Promise.resolve(null);
  if (win.html2canvas) return Promise.resolve(win.html2canvas);
  if (html2canvasLoading) return html2canvasLoading;
  html2canvasLoading = new Promise((resolve) => {
    const script = doc.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
    script.onload = () => resolve(win.html2canvas || null);
    script.onerror = () => resolve(null);
    doc.head.appendChild(script);
  });
  return html2canvasLoading;
}

async function captureViewportScreenshot(frame: HTMLIFrameElement | null): Promise<ViewportScreenshot> {
  const win = frameWindow(frame);
  const doc = frameDocument(frame);
  const html2canvas = await loadHtml2canvas(doc);
  if (!html2canvas) return { ok: false, error: 'html2canvas unavailable' };
  try {
    const canvas = await html2canvas(doc.body, {
      scale: 0.75,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: win.innerWidth,
      windowHeight: win.innerHeight,
      x: win.scrollX,
      y: win.scrollY,
      width: win.innerWidth,
      height: win.innerHeight,
    });
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.includes('base64,')
      ? dataUrl.substring(dataUrl.indexOf('base64,') + 'base64,'.length)
      : dataUrl;
    return {
      ok: true,
      base64,
      mimeType: 'image/png',
      widthPx: canvas.width,
      heightPx: canvas.height,
    };
  } catch (error) {
    return { ok: false, error: (error as Error).message || String(error) };
  }
}

function canAccessFrameDocument(frame: HTMLIFrameElement | null): boolean {
  try {
    void frameWindow(frame).document?.title;
    return true;
  } catch {
    return false;
  }
}

function frameOrigin(frame: HTMLIFrameElement | null): string {
  const src = frame?.src || frame?.getAttribute('src') || '';
  if (!src) throw new Error('App iframe has no source URL');
  try {
    return new URL(src, window.location.href).origin;
  } catch {
    throw new Error(`App iframe has invalid source URL: ${src}`);
  }
}

function executePageCommandViaBridge(frame: HTMLIFrameElement | null, action: string, rawPayload: unknown): Promise<PageCommandResult> {
  const target = frameWindow(frame);
  const targetOrigin = frameOrigin(frame);
  const requestId = `page-command-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const timeoutMs = action === 'request_screenshot' || action === 'screenshot'
    ? SCREENSHOT_COMMAND_TIMEOUT_MS
    : PAGE_COMMAND_TIMEOUT_MS;

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error(`Timed out waiting for ${targetOrigin} page-command bridge`));
    }, timeoutMs);

    function settle(fn: () => void) {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      fn();
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== target) return;
      if (event.origin !== targetOrigin) return;
      const data = event.data as { type?: string; requestId?: string; ok?: boolean; result?: PageCommandResult; error?: string } | null;
      if (!data || data.type !== PAGE_COMMAND_RESULT_MESSAGE || data.requestId !== requestId) return;
      if (data.ok) {
        settle(() => resolve(data.result as PageCommandResult));
      } else {
        settle(() => reject(new Error(data.error || 'Page command failed in iframe bridge')));
      }
    }

    window.addEventListener('message', onMessage);
    target.postMessage({ type: PAGE_COMMAND_MESSAGE, requestId, action, payload: rawPayload }, targetOrigin);
  });
}

function pageCommandHttpViaBridge(
  frame: HTMLIFrameElement | null,
  method: string,
  path: string,
  body?: string,
): Promise<{ ok: boolean; status: number; body: unknown; error?: string }> {
  const target = frameWindow(frame);
  const targetOrigin = frameOrigin(frame);
  const requestId = `page-command-http-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error(`Timed out waiting for ${targetOrigin} page-command HTTP bridge`));
    }, PAGE_COMMAND_TIMEOUT_MS);

    function settle(fn: () => void) {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      fn();
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== target) return;
      if (event.origin !== targetOrigin) return;
      const data = event.data as { type?: string; requestId?: string; ok?: boolean; status?: number; body?: unknown; error?: string } | null;
      if (!data || data.type !== PAGE_COMMAND_HTTP_RESULT_MESSAGE || data.requestId !== requestId) return;
      settle(() => resolve({
        ok: !!data.ok,
        status: typeof data.status === 'number' ? data.status : 0,
        body: data.body,
        error: data.error,
      }));
    }

    window.addEventListener('message', onMessage);
    target.postMessage({
      type: PAGE_COMMAND_HTTP_MESSAGE,
      requestId,
      method,
      path,
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body,
    }, targetOrigin);
  });
}

async function executePageCommandDirect(frame: HTMLIFrameElement | null, action: string, rawPayload: unknown): Promise<PageCommandResult> {
  const payload = commandPayload(action, rawPayload);
  const win = frameWindow(frame);
  const doc = frameDocument(frame);

  switch (action) {
    case 'snapshot':
      return buildSnapshot(frame);
    case 'getUrl':
      return win.location.href;
    case 'navigate': {
      const url = normalizeNavigateUrl(win, payload);
      win.location.href = url;
      return { ok: true, url };
    }
    case 'click': {
      const element = findElement(doc, textPayload(payload));
      if (!element) return { ok: false, error: `Element not found: ${textPayload(payload)}` };
      element.click();
      return { ok: true, text: (element.textContent || (element as HTMLInputElement).value || '').trim().slice(0, 100) };
    }
    case 'fill': {
      const parsed = payload && typeof payload === 'object' ? payload as Record<string, unknown> : null;
      const text = textPayload(payload);
      const selector = String(parsed?.selector || (text.includes('|') ? text.split('|')[0] : ''));
      const value = String(parsed?.value || (text.includes('|') ? text.split('|').slice(1).join('|') : ''));
      const element = findElement(doc, selector) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (!element) return { ok: false, error: `Element not found: ${selector}` };
      element.value = value;
      element.dispatchEvent(new Event('input', { bubbles: true }));
      element.dispatchEvent(new Event('change', { bubbles: true }));
      return { ok: true, selector, value };
    }
    case 'querySelector': {
      const selector = textPayload(payload);
      return Array.from(doc.querySelectorAll(selector)).slice(0, 20).map((element) => ({
        tag: element.tagName.toLowerCase(),
        id: (element as HTMLElement).id || undefined,
        text: element.textContent?.trim().slice(0, 200),
        value: (element as HTMLInputElement).value || undefined,
        href: (element as HTMLAnchorElement).href || undefined,
      }));
    }
    case 'getGridData':
      return getGridData(frame);
    case 'request_screenshot':
    case 'screenshot':
      return captureViewportScreenshot(frame);
    default:
      return { ok: false, error: `Unknown page command: ${action}` };
  }
}

export async function executePageCommand(frame: HTMLIFrameElement | null, action: string, rawPayload: unknown): Promise<PageCommandResult> {
  if (canAccessFrameDocument(frame)) return executePageCommandDirect(frame, action, rawPayload);
  return executePageCommandViaBridge(frame, action, rawPayload);
}

function apiUrl(path: string, baseUrl?: string | null): string {
  if (!baseUrl) return path;
  return new URL(path, baseUrl).href;
}

function isSameOrigin(baseUrl?: string | null): boolean {
  if (!baseUrl) return true;
  try {
    return new URL(baseUrl, window.location.href).origin === window.location.origin;
  } catch {
    return true;
  }
}

export async function pollPendingPageCommand(
  sessionId: string,
  baseUrl?: string | null,
  frame?: HTMLIFrameElement | null,
): Promise<PendingPageCommand> {
  const path = `/api/v1/AIAssistant/PageCommand/Pending?sessionId=${encodeURIComponent(sessionId)}`;
  if (!isSameOrigin(baseUrl) && frame?.contentWindow) {
    const bridged = await pageCommandHttpViaBridge(frame, 'GET', path);
    if (!bridged.ok) throw new Error(`poll failed: HTTP ${bridged.status}${bridged.error ? ` ${bridged.error}` : ''}`);
    return bridged.body as PendingPageCommand;
  }
  const url = apiUrl(path, baseUrl);
  const response = await fetch(url, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`poll failed: HTTP ${response.status}`);
  return response.json() as Promise<PendingPageCommand>;
}

export async function postPageCommandResult(
  commandId: string,
  result: PageCommandResult,
  baseUrl?: string | null,
  frame?: HTMLIFrameElement | null,
): Promise<void> {
  const path = `/api/v1/AIAssistant/PageCommand/${encodeURIComponent(commandId)}/Result`;
  const body = JSON.stringify({ result: typeof result === 'string' ? result : JSON.stringify(result) });
  if (!isSameOrigin(baseUrl) && frame?.contentWindow) {
    const bridged = await pageCommandHttpViaBridge(frame, 'POST', path, body);
    if (!bridged.ok) throw new Error(`post result failed: HTTP ${bridged.status}${bridged.error ? ` ${bridged.error}` : ''}`);
    return;
  }
  const url = apiUrl(path, baseUrl);
  await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
}
